// Mirrored in applications/platform-app/components/web-analytics — change both.
import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { RubyLoader } from '../RubyLoader';
import { RubyGemMark } from '../RubyGemMark';
import { RUBY, RUBY_TINT } from './format';

/** Small building blocks shared by every tab — ruby on light, ported from the mockup. */

export const Card: React.FC<{ title?: React.ReactNode; action?: React.ReactNode; className?: string; style?: React.CSSProperties; children?: React.ReactNode; testId?: string }> = ({ title, action, className = '', style, children, testId }) => (
  <div className={`bg-white border border-stone-200 rounded-2xl p-4 min-w-0 text-stone-900 ${className}`} style={style} data-testid={testId}>
    {(title || action) && (
      <h3 className="m-0 mb-3 text-[13px] font-semibold flex items-center gap-2">
        {title}
        {action && <span className="ml-auto">{action}</span>}
      </h3>
    )}
    {children}
  </div>
);

export const MoreButton: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <button type="button" onClick={onClick} className="text-xs font-semibold min-h-[44px] px-1 bg-transparent border-0" style={{ color: RUBY }}>
    {children}
  </button>
);

export const Muted: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <span className={`text-stone-500 font-normal ${className}`}>{children}</span>
);

export const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[11px] text-stone-500 mt-2">{children}</div>
);

type TagTone = 'red' | 'amber' | 'green' | 'gray';
const TAG: Record<TagTone, string> = {
  red: 'bg-[#FFF1F3] text-[#9F1239]',
  amber: 'bg-amber-100 text-amber-800',
  green: 'bg-green-100 text-green-800',
  gray: 'bg-stone-100 text-stone-700',
};
export const Tag: React.FC<{ tone: TagTone; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${TAG[tone]}`}>{children}</span>
);
export const threatTone = (level: string): TagTone => (level === 'High' ? 'red' : level === 'Elevated' ? 'amber' : 'green');
export const severityTone = (s: string): TagTone => (s === 'High' ? 'red' : s === 'Medium' ? 'amber' : 'gray');

export const Switch: React.FC<{ on: boolean; onChange: (v: boolean) => void; label?: React.ReactNode; disabled?: boolean; testId?: string }> = ({ on, onChange, label, disabled, testId }) => (
  <button
    type="button" role="switch" aria-checked={on} disabled={disabled} data-testid={testId}
    onClick={() => onChange(!on)}
    className="inline-flex items-center gap-2 text-xs text-stone-700 min-h-[44px] bg-transparent border-0 select-none disabled:opacity-50"
  >
    <span className="relative inline-block w-9 h-5 rounded-full transition-colors" style={{ background: on ? RUBY : '#d6d3d1' }}>
      <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all" style={{ left: on ? 18 : 2 }} />
    </span>
    {label}
  </button>
);

export const Button: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'ruby' | 'plain'; size?: 'sm' | 'md' }> = ({ tone = 'plain', size = 'md', className = '', children, ...rest }) => (
  <button
    type="button"
    {...rest}
    className={`${size === 'sm' ? 'h-11 px-3 text-[11px]' : 'h-11 px-4 text-xs'} min-w-[44px] rounded-[9px] font-semibold border inline-flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed ${tone === 'ruby' ? 'text-white' : 'bg-white text-stone-900 border-stone-300 hover:bg-stone-50'} ${className}`}
    style={tone === 'ruby' ? { background: RUBY, borderColor: RUBY } : undefined}
  >
    {children}
  </button>
);

export const Empty: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div className="text-xs text-stone-500 py-6 text-center">{children || 'No data yet for this period'}</div>
);

export const ErrorLine: React.FC<{ message: string }> = ({ message }) => (
  <div className="text-xs py-3" style={{ color: '#9F1239' }} role="alert">{message}</div>
);

/** Busy state inside a button / chip — the drawing gem in currentColor. */
export const BusyGem: React.FC<{ size?: number }> = ({ size = 14 }) => <RubyGemMark size={size} drawing color="currentColor" />;

/** The ruby gem over a region while it loads — transparent, no backdrop, no caption. */
export const LoadingOverlay: React.FC<{ on: boolean }> = ({ on }) => (on ? (
  <div className="absolute inset-0 z-20 flex justify-center pointer-events-none" style={{ paddingTop: 120 }} data-testid="wa-loading">
    <RubyLoader variant="section" label="Loading analytics" />
  </div>
) : null);

/** Modal shell: black header bar, white body, footer row. Escape / scrim click close. */
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal shell: black header bar, white body, footer row. Escape / scrim click close.
 * Focus moves into the dialog on open (first body control, else the close button),
 * Tab / Shift-Tab are trapped inside, and focus returns to the opener on close.
 */
export const Modal: React.FC<{ title: React.ReactNode; badge?: React.ReactNode; onClose: () => void; footer?: React.ReactNode; children: React.ReactNode; width?: number; testId?: string }> = ({ title, badge, onClose, footer, children, width = 560, testId }) => {
  const dialog = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const titleId = useId();

  // Mount-only: open focus, Escape, and focus restore on close (onClose read through a ref so a re-render never re-runs it).
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const el = dialog.current;
    if (el && !el.contains(document.activeElement)) {
      const first = body.current?.querySelector<HTMLElement>(FOCUSABLE) || el.querySelector<HTMLElement>(FOCUSABLE) || el;
      first.focus();
    }
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current(); };
    window.addEventListener('keydown', k);
    return () => {
      window.removeEventListener('keydown', k);
      if (opener && document.contains(opener) && typeof opener.focus === 'function') opener.focus();
    };
  }, []);

  const trap = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !dialog.current) return;
    const items = Array.from(dialog.current.querySelectorAll<HTMLElement>(FOCUSABLE)) as HTMLElement[];
    if (!items.length) { e.preventDefault(); return; }
    const first = items[0], last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === dialog.current)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  };

  const node = (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,.45)' }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        ref={dialog} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={trap} data-testid={testId}
        className="bg-white rounded-2xl max-w-full overflow-auto text-stone-900 outline-none" style={{ width, maxHeight: '90vh' }}
      >
        <div className="bg-black text-white px-[18px] py-3.5 flex items-center gap-2.5 rounded-t-2xl">
          <h2 id={titleId} className="m-0 text-[15px] font-semibold">{title}</h2>
          {badge}
          <button type="button" aria-label="Close" onClick={onClose} className="ml-auto bg-[#1f1f1f] border-0 text-white w-11 h-11 rounded-lg inline-flex items-center justify-center"><X size={16} /></button>
        </div>
        <div ref={body} className="p-[18px] text-[13px]">{children}</div>
        {footer && <div className="px-[18px] py-3 border-t border-stone-200 flex gap-2 justify-end">{footer}</div>}
      </div>
    </div>
  );
  return typeof document !== 'undefined' ? createPortal(node, document.body) : node;
};

/** Option pills used in modals (type / scope / duration). */
export function OptionPills<T extends string>({ value, options, onChange, testId }: { value: T; options: Array<{ id: T; label: string; disabled?: boolean }>; onChange: (v: T) => void; testId?: string }) {
  return (
    <div className="flex gap-1.5 flex-wrap" data-testid={testId}>
      {options.map((o) => {
        const on = o.id === value;
        return (
          <button
            key={o.id} type="button" disabled={o.disabled} aria-pressed={on}
            onClick={() => onChange(o.id)}
            className="h-11 px-3.5 rounded-[9px] border text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            style={on ? { borderColor: RUBY, background: RUBY_TINT, color: '#9F1239' } : { borderColor: '#d6d3d1', background: '#fff', color: '#44403c' }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="mb-3.5">
    <label className="block text-[11px] font-semibold text-stone-500 mb-1.5">{label}</label>
    {children}
  </div>
);

export const TextInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(({ invalid, className = '', ...rest }, ref) => (
  <input
    ref={ref}
    {...rest}
    className={`w-full h-11 border rounded-[10px] px-3 text-[13px] text-stone-900 bg-white outline-none focus:ring-4 focus:ring-[#FFF1F3] ${invalid ? 'border-[#DC143C]' : 'border-stone-300 focus:border-[#DC143C]'} ${className}`}
  />
));
TextInput.displayName = 'TextInput';

/** Bottom toast — black, ruby left edge (mockup `.toast`). */
export const Toast: React.FC<{ text: string | null }> = ({ text }) => (
  <div
    role="status" aria-live="polite"
    className="fixed left-1/2 bottom-[22px] z-[1100] bg-[#111] text-white px-[18px] py-3 rounded-[10px] text-xs transition-transform duration-300 max-w-[90vw]"
    style={{ borderLeft: `3px solid ${RUBY}`, transform: `translateX(-50%) translateY(${text ? 0 : 120}px)`, visibility: text ? 'visible' : 'hidden' }}
  >
    {text}
  </div>
);

/**
 * Props that make a table row a keyboard-reachable button (Tab to it, Enter / Space opens).
 * `undefined` → a plain, non-interactive row.
 */
export function rowButton(onClick: (() => void) | undefined, label?: string): React.HTMLAttributes<HTMLTableRowElement> {
  if (!onClick) return {};
  return {
    onClick,
    role: 'button',
    tabIndex: 0,
    ...(label ? { 'aria-label': label } : {}),
    className: 'cursor-pointer hover:bg-stone-50 focus:outline-none focus-visible:bg-[#FFF1F3]',
    onKeyDown: (e) => {
      if (e.target !== e.currentTarget) return; // a button inside the row handles its own keys
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
    },
  };
}

export const Th: React.FC<{ children?: React.ReactNode; num?: boolean }> = ({ children, num }) => (
  <th className={`text-[11px] text-stone-500 font-semibold px-2 py-2 border-b border-stone-200 whitespace-nowrap ${num ? 'text-right' : 'text-left'}`}>{children}</th>
);
export const Td: React.FC<{ children?: React.ReactNode; num?: boolean; className?: string; style?: React.CSSProperties; colSpan?: number }> = ({ children, num, className = '', style, colSpan }) => (
  <td colSpan={colSpan} className={`px-2 py-[9px] border-b border-stone-100 whitespace-nowrap ${num ? 'text-right tabular-nums' : ''} ${className}`} style={style}>{children}</td>
);
